// The three lab write calls and their optimistic row patches, in one place.
//
// Stateless on purpose: each page keeps its own `rows`/`openRow` state and calls
// these from a thin useCallback. A stateful hook was considered and rejected —
// none of the writers read the row lists (they only need the row being edited),
// and the maker-picker flow is inseparable from Lab.jsx's own <MakerPicker> block.
//
// ⚠️ THE RULE THIS MODULE EXISTS FOR: every write goes to Xano's `POST /lab`, an
// upsert where unsent fields fall back to the existing row via first_notempty —
// EXCEPT `detail`, which has NO fallback. `functions/api/lab/price.js` builds
// `detail: String(body?.detail ?? '')`, so a caller that omits it BLANKS the
// column. Every writer below forwards `row.detail`. Duplicating these calls at a
// second call site is how that gets lost.
import { apiDelete, postJson } from './api.js'

// The detail drawer's ONE write — see functions/api/lab/edit.js. `assign`/`price`/
// `retainer` were always three doors onto the same upstream POST /lab, so the drawer's
// single บันทึก sends everything at once and cannot half-save. They stay for their other
// callers (the /lab ledger's inline price cell, chiefly).
export const postLabEdit = (row, payload) =>
  postJson('/api/lab/edit', { lab_id: row.id, detail: '', ...payload })

// CREATE. Separate endpoint, not `assign` with a missing lab_id: the required-field
// rules are different (patient + treatment + vendor) and one of them is not obvious —
// `treatment_id` must be non-zero or the row is written and then never returned by
// GET /lab, which RIGHT-joins treatment. Returns the new lab row.
export const postLabCreate = (fields) => postJson('/api/lab/record', fields)

// DELETE — ⚠️ HARD. The row, its price split and its stage history go with it.
// `patient_id` is required by the proxy: it is how the server re-reads the row's stage
// to refuse a lab already on หักยอดทันตแพทย์แล้ว, and it fails closed.
export const deleteLab = (row) =>
  apiDelete(`/api/lab/record?lab_id=${Number(row.id)}&patient_id=${Number(row.patient_id) || 0}`)

/* ── pure row → row patches ────────────────────────────────────────────────── */

// Stage change. `appendLog` mirrors what Xano appends server-side so an open
// drawer's timeline updates without a refetch; list rows don't need it.
export function applyStagePatch(row, stageId, stageById, employeeId, { appendLog = false } = {}) {
  const s = stageById?.[Number(stageId)]
  const badge = s ? { name: s.name, color: s.color } : undefined
  const next = { ...row, labstage_id: Number(stageId), labstage: badge || row.labstage }
  if (appendLog) {
    const log = Array.isArray(row.log) ? row.log.slice() : []
    log.push({ timestamp: Date.now(), type: 'Stage Change', labstage_id: Number(stageId), employee_id: employeeId || 0 })
    next.log = log
  }
  return next
}

// Assignment edit. Resolves the new vendor name and doctor code/image from the
// already-loaded reference lists so the row renders correctly before a refetch.
// No longer exported for its own sake — applyEditPatch below is the only caller.
export function applyInfoPatch(row, fields, { vendorMap, doctors } = {}) {
  const next = { ...row }
  if (fields.lablist_id != null) {
    next.lablist_id = Number(fields.lablist_id)
    const v = vendorMap?.get?.(Number(fields.lablist_id))
    if (v) next.lablist = { name: v.name }
  }
  if (fields.repair != null) next.repair = fields.repair
  if (fields.detail != null) next.detail = fields.detail
  if (fields.date != null) next.date = fields.date
  if (fields.year != null) next.year = Number(fields.year)
  if (fields.month != null) next.month = Number(fields.month)
  // The two links. Nothing to resolve from a reference list — the drawer refetches
  // the treatment / appointment itself off the new id.
  if (fields.treatment_id != null) next.treatment_id = Number(fields.treatment_id)
  if (fields.appointment_id != null) next.appointment_id = Number(fields.appointment_id)
  if (fields.doctor_id != null) {
    next.doctor_id = Number(fields.doctor_id)
    const d = doctors?.find((x) => String(x.id) === String(fields.doctor_id))
    if (d) next.doctor = { code: d.code || [d.name, d.surname].filter(Boolean).join(' '), profile_img: d.profile_img || '' }
  }
  return next
}

// The whole-drawer edit, applied optimistically. Reuses applyInfoPatch for the fields it
// already knows how to resolve (vendor name, doctor code/image) and layers the three the
// unified save added. `payload` is exactly what went to /api/lab/edit, so a key that is
// absent means "left alone" here too.
export function applyEditPatch(row, payload, refs) {
  let next = applyInfoPatch(row, payload, refs)
  if (payload.detail != null) next.detail = payload.detail
  if (payload.price) next = { ...next, price: payload.price }
  if (payload.production_type != null) next.production_type = payload.production_type
  if (payload.retainer) next.retainer = payload.retainer
  return next
}
